import { useRef, useEffect, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Spinner, Alert } from 'react-bootstrap';

const FaceRecognition = ({ onCapture, isProcessing }) => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraError, setCameraError] = useState(null);
  const [faceStatus, setFaceStatus] = useState("Aguardando rosto...");
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const isCapturingRef = useRef(false);
  const faceDetectorRef = useRef(null);
  const animFrameRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const initDetector = async () => {
      try {
        const { FaceDetector, FilesetResolver } = await import(
          /* webpackIgnore: true */
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.33/vision_bundle.mjs'
        );

        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
        );

        const detector = await FaceDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite",
            delegate: "GPU"
          },
          runningMode: "VIDEO",
          minDetectionConfidence: 0.5
        });

        if (isMounted) {
          faceDetectorRef.current = detector;
          setIsModelLoaded(true);
        }
      } catch (err) {
        console.error("Erro ao inicializar detector:", err);
        if (isMounted) setCameraError("Erro ao carregar modelo de detecção facial.");
      }
    };

    initDetector();

    return () => {
      isMounted = false;
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (faceDetectorRef.current) faceDetectorRef.current.close();
    };
  }, []);

  const detect = useCallback(() => {
    const video = webcamRef.current?.video;
    const canvas = canvasRef.current;

    if (!faceDetectorRef.current || !video || !canvas || video.readyState !== 4) {
      animFrameRef.current = requestAnimationFrame(detect);
      return;
    }

    if (isCapturingRef.current || isProcessing) {
      animFrameRef.current = requestAnimationFrame(detect);
      return;
    }

    const { videoWidth: width, videoHeight: height } = video;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(video, 0, 0, width, height);

    const result = faceDetectorRef.current.detectForVideo(video, performance.now());

    if (result.detections.length > 0) {
      const detection = result.detections[0];
      const score = detection.categories[0].score;
      const box = detection.boundingBox; // pixels: originX, originY, width, height

      ctx.strokeStyle = '#00BFA5';
      ctx.lineWidth = 4;
      ctx.strokeRect(box.originX, box.originY, box.width, box.height);

      const centerX = (box.originX + box.width / 2) / width;
      const centerY = (box.originY + box.height / 2) / height;
      const normalizedWidth = box.width / width;

      const isCentered = Math.abs(centerX - 0.5) < 0.15 && Math.abs(centerY - 0.5) < 0.15;
      const isCloseEnough = normalizedWidth > 0.25;

      if (score > 0.85 && isCentered && isCloseEnough) {
        setFaceStatus("Rosto identificado! Capturando...");
        isCapturingRef.current = true;
        setTimeout(() => {
          const imageSrc = webcamRef.current.getScreenshot();
          onCapture(imageSrc);
        }, 500);
        return;
      } else if (!isCentered) {
        setFaceStatus("Centralize seu rosto...");
      } else if (!isCloseEnough) {
        setFaceStatus("Aproxime-se da câmera...");
      } else {
        setFaceStatus("Mantenha a posição...");
      }
    } else {
      setFaceStatus("Nenhum rosto detectado.");
    }

    animFrameRef.current = requestAnimationFrame(detect);
  }, [onCapture, isProcessing]);

  useEffect(() => {
    if (isModelLoaded) {
      animFrameRef.current = requestAnimationFrame(detect);
    }
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isModelLoaded, detect]);

  return (
    <div className="d-flex flex-column align-items-center">
      {cameraError && <Alert variant="danger">{cameraError}</Alert>}

      {!isModelLoaded && !cameraError && (
        <div className="text-center my-3">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Iniciando câmera e IA...</p>
        </div>
      )}

      <div style={{ position: 'relative', width: 640, height: 480, maxWidth: '100%', borderRadius: '12px', overflow: 'hidden' }}>
        <Webcam
          ref={webcamRef}
          audio={false}
          width={640}
          height={480}
          screenshotFormat="image/jpeg"
          videoConstraints={{ facingMode: "user" }}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onUserMediaError={() => setCameraError("Permissão de câmera negada.")}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }}
        />
      </div>

      <div className="mt-3 text-center">
        <h5 className={`mb-2 ${faceStatus.includes("Capturando") ? "text-success fw-bold" : "text-secondary"}`}>
          {faceStatus}
        </h5>
        {isProcessing && <Spinner animation="border" size="sm" />}
      </div>
    </div>
  );
};

export default FaceRecognition;
